import type React from "react";
import { useState } from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { produce } from "immer";
import Dropzone from "react-dropzone";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";

import { logger } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import {
  MAX_DOC_UPLOAD_DOCS_ALLOWED,
  QUERY_STRING_DOC_SELECTED,
  ACCEPTED_DOC_TYPES,
} from "../../constants";
import { SegmentEnum, VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { addFiles, removeFile } from "../../lib/ServerApi/VerificationRequestSetters";
import { validateFieldById } from "../../lib/validators/validators";
import {
  type VerificationService,
  type DocUploadViewModel,
  type ErrorId,
  type DocUploadResponse,
  type FieldValidationErrors,
} from "../../lib/types/types";
import { LogoComponent } from "../LogoComponent/LogoComponent";
import { FormFooterComponent as FormFooter } from "../FormFooter/FormFooterComponent";
import { getEstAndMaxReviewTimes } from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { RewardsRemainingComponent } from "../RewardsRemaining/RewardsRemainingComponent";
import { getQueryOrOption } from "../../lib/ServerApi/TestingRouteOverrides";
import ExpandingSection from "../ExpandingSection/ExpandingSection";
import { FaqLinkComponent } from "../FaqLink/FaqLinkComponent";
import { getRefByFieldId, setRef } from "../../lib/refs/refs";
import { useFlag } from "../FeatureFlags/hooks";
import {
  DOC_UPLOAD_SUBMISSION_TIME,
  DOC_UPLOAD_SUBMIT,
  useMetrics,
} from "../FeatureFlags/useMetrics";
import { getLegacyUploadInfo } from "../UploadInfoComponent/getLegacyUploadInfo";
import { AnnouncingHeader } from "../AnnouncingHeader/AnnouncingHeader";
import { EventName, recordCTAClickEvent } from "../../lib/GoogleAnalytics/ga";

import { DocumentTypeUpload, STUDENT_DOC_TYPES } from "./DocumentTypeUpload";
import { NamedDivider } from "./NamedDivider";
import { RequirementsChecklist } from "./RequirementsChecklist";
import { getLegacyAcceptableUploads } from "./getLegacyAcceptableUploads";
import { getAcceptableUploads, getUploadInfo } from "./getAcceptableInfoUploads";

const GERMANY_COUNTRY_CODE = "DE";
interface StepDocUploadProps {
  verificationService: VerificationService;
  intl: any;
}

interface ImagePreview {
  name: string;
  url: string;
}

const ConditionalErrorMsg: React.FC<{
  errorId: ErrorId;
  erroredFileNames: string[];
  fieldValidationErrors: FieldValidationErrors;
}> = ({ errorId, erroredFileNames, fieldValidationErrors }) => {
  if (!errorId) return null;

  return (
    <div className="sid-field-error sid-field-error-redesign__doc-upload">
      <FormattedHTMLMessage
        id={`errorId.${fieldValidationErrors.docUpload}`}
        defaultMessage="No valid files"
      />
      {erroredFileNames
        ? erroredFileNames.map((filename) => (
            <div key={`error-${filename}`} className="sid-field-error-redesign__doc-upload__span">
              {filename}
            </div>
          ))
        : null}
    </div>
  );
};

const StepDocUploadRedesign = ({ verificationService, intl }: StepDocUploadProps) => {
  const [addingAnother, setAddingAnother] = useState(false);

  const verificationResponse: DocUploadResponse =
    verificationService.verificationResponse as DocUploadResponse;
  const { rejectionReasons, segment, subSegment, verificationId, locale, country } =
    verificationResponse;
  const { programTheme, programId } = verificationService;
  const viewModel: DocUploadViewModel = verificationService.viewModel as DocUploadViewModel;
  const { fieldValidationErrors } = verificationService;
  const { overrideEnabled } = verificationService.programTheme;
  const useLegacyUploadMessages = useFlag("doc-upload-redesign-use-legacy-message-keys");
  const flagDocConsiderations: string = useFlag("doc-upload-considerations");
  const uploadInfo = (useLegacyUploadMessages ? getLegacyUploadInfo : getUploadInfo)(
    segment,
    subSegment,
  );
  const isUSOnly = verificationService.verificationResponse.country === "US";
  const getFileForView = (file: File) => ({
    name: file.name,
    url: file.type.includes("image") ? URL.createObjectURL(file) : "",
  });

  // Part of may24 variation doc-upload-types.
  // Kind of a hack ot track what types are attached. Should be filename: type.
  const [attachedDocumentTypes, setAttachedDocumentTypes] = useState<Record<string, string>>({});

  const { userDidConvert } = useMetrics({ segment });

  const currentFileNames: ImagePreview[] = [];

  const may24VariationRaw = useFlag("doc-upload-may24");
  // Filter out non-student/en-US from the variations. This shouldn't be
  // completely necessary as the LD targeting will account for this, but it
  // makes it so we can test other segments to make sure nothing leaks.
  const may24Variation =
    verificationResponse.segment === "student" && verificationResponse.locale === "en-US"
      ? may24VariationRaw
      : "default";

  const flag = useFlag("docUpload-assertion-checklist");
  const assertionCheckListFlag = flag === "checklist";
  const [assertionCheckState, setAssertionCheckState] = useState<string[]>([]);
  const [showAssertionCheckErrors, setShowAssertionCheckErrors] = useState<boolean>(false);

  const isMockingFileSelected = getQueryOrOption(QUERY_STRING_DOC_SELECTED);
  if (isMockingFileSelected) {
    currentFileNames.push({
      name: "myFile.png",
      url: "",
    });
  }
  const addBtnAriaLabel = intl.formatMessage({
    id: "step.docUpload.addFile",
    defaultMessage: "Add documents",
  });

  if (viewModel.file1 && viewModel.file1.name) {
    currentFileNames.push(getFileForView(viewModel.file1));
  }
  if (viewModel.file2 && viewModel.file2.name) {
    currentFileNames.push(getFileForView(viewModel.file2));
  }
  if (viewModel.file3 && viewModel.file3.name) {
    currentFileNames.push(getFileForView(viewModel.file3));
  }

  // Part of may24 variation doc-upload-types.
  // Returns an string[] of attached document types. It is possible to have duplicates.
  const currentAttachedDocumentTypes = currentFileNames.map((ip) => attachedDocumentTypes[ip.name]);

  const addFilesAndUpdate = (files: File[], erroredFilenames?: string[]) => {
    recordCTAClickEvent(EventName.DOC_UPLOAD_ADD_FILES, {
      programId,
      locale,
      verificationId,
    });

    setAddingAnother(false);
    verificationService.updateViewModel(addFiles(viewModel, files, erroredFilenames));
  };

  const removeFileAndUpdate = (arrayIndex: number) => {
    recordCTAClickEvent(EventName.DOC_UPLOAD_REMOVE_FILES, {
      programId,
      locale,
      verificationId,
    });

    const fileNumber = arrayIndex + 1;
    setAddingAnother(false);
    setAssertionCheckState([]);
    setShowAssertionCheckErrors(false);
    verificationService.updateViewModel(removeFile(viewModel, fileNumber));
  };
  const errorId =
    fieldValidationErrors.docUpload ||
    getSafe(() => verificationService.verificationResponse.errorIds[0], undefined);
  const updateFieldValidationErrorsAndAddFiles = (files: File[], type?: string) => {
    const acceptedFiles: File[] = [];
    const errors: ErrorId[] = [];
    const erroredFilenames: string[] = [];

    for (const file of files) {
      const fileValidationError: ErrorId = validateFieldById(
        VerificationStepsEnum.docUpload,
        file,
        verificationService.formValidationOptions,
      ) as ErrorId;

      if (fileValidationError === undefined) {
        if (type) {
          setAttachedDocumentTypes({ ...attachedDocumentTypes, [file.name]: type });
        }
        acceptedFiles.push(file);
      } else {
        erroredFilenames.push(file.name);
        errors.push(fileValidationError);
      }
    }

    let nextFieldValidationErrors;
    if (errors) {
      nextFieldValidationErrors = produce(fieldValidationErrors, (draft) => {
        draft.docUpload = errors[0]; // we only care about one error at a time
      });
    }

    verificationService.updateFieldValidationErrors(nextFieldValidationErrors);
    addFilesAndUpdate(acceptedFiles, erroredFilenames);
  };

  const submitForm = () => {
    logger.info("StepDocUploadComponent submitting form");
    setShowAssertionCheckErrors(true);

    if (
      (assertionCheckListFlag && uploadInfo.every((req) => assertionCheckState.includes(req))) ||
      !assertionCheckListFlag
    ) {
      verificationService.submitStep(
        VerificationStepsEnum.docUpload,
        viewModel,
        verificationService.verificationResponse,
      );
      userDidConvert(DOC_UPLOAD_SUBMIT, DOC_UPLOAD_SUBMISSION_TIME);
    }
  };

  const onFileDrop = (draggedFiles: File[], type?: string) => {
    if (draggedFiles.length > 0) {
      updateFieldValidationErrorsAndAddFiles(draggedFiles, type);
    } else if (draggedFiles.length <= 0) {
      logger.error(
        "No files were accepted or rejected. Unknown error.",
        VerificationStepsEnum.docUpload,
      );
    }
  };

  const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFileDrop(Array.from(e.target.files));
  };

  const handleAddFileOnKeyUp = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    const addFileRef = getRefByFieldId("addFile");

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      addFileRef.click();
    }
  };

  const cancelDocUpload = async () => {
    logger.info("StepDocUploadComponent submitting form after skipping document upload");
    const nextState = produce(viewModel, () => {});
    verificationService.submitStep(
      VerificationStepsEnum.cancelDocUpload,
      nextState,
      verificationService.verificationResponse,
    );
  };

  const uploadZone = (
    <div className="sid-upload-wrap sid-l-space-top-md sid-l-space-btm-md">
      {may24Variation === "doc-type-icons" ? (
        <>
          {/* doc-type-icons is hard coded to 2 student document types for now.  */}
          {STUDENT_DOC_TYPES.map(
            (type) =>
              !currentAttachedDocumentTypes.includes(type) && (
                <DocumentTypeUpload
                  onFileDrop={onFileDrop}
                  type={type}
                  style={type === "ID_CARD" && currentFileNames.length === 0 ? "large" : "small"}
                />
              ),
          )}
        </>
      ) : (
        <>
          <ConditionalErrorMsg
            fieldValidationErrors={fieldValidationErrors}
            errorId={errorId}
            erroredFileNames={viewModel.erroredFileNames}
          />
          <div className="sid-docdrop-wrap">
            <Dropzone onDrop={(files) => onFileDrop(files)} accept={ACCEPTED_DOC_TYPES.join(",")}>
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  className="sid-docdrop-wrap__dropzone sid-docdrop-wrap__dropzone_height"
                >
                  <input {...getInputProps({ accept: ACCEPTED_DOC_TYPES.join(",") })} />
                  <button
                    // this `tabindex=-1` prevents a double focus situation
                    // here. The parent dropzone `...getRootProps`
                    // makes the parent div focusable AND allows files to be
                    // dropped on the "button". Acting on that div works exactly
                    // the same as acting on this button but having both take
                    // tab focus is confusing and problematic.
                    tabIndex={-1}
                    className="sid-btn sid-btn--dark sid-doc-upload-submit-btn sid-submit__continue border-radius"
                    type="button"
                    aria-label={addBtnAriaLabel}
                  >
                    <FormattedHTMLMessage
                      id="step.docUpload.addFile"
                      defaultMessage="Add documents"
                    />
                  </button>
                </div>
              )}
            </Dropzone>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      id="sid-step-doc-upload"
      className={`sid-doc-upload-redesign-container sid-l-container sid-may24-variation__${may24Variation}`}
    >
      <div role="main">
        <div className="sid-header sid-l-space-top-md">
          <div className="sid-doc-upload-logo-container sid-l-horz-center">
            <LogoComponent verificationService={verificationService} />
          </div>
          <AnnouncingHeader align="start">
            <FormattedHTMLMessage id="step.docUpload.title" tagName="h1" />
          </AnnouncingHeader>

          <RewardsRemainingComponent verificationService={verificationService} />

          {rejectionReasons && rejectionReasons.length > 0 && (
            <div className="sid-upload-rejection__wrapper">
              <div className="sid-header__subtitle-doc-upload-rejected">
                <FormattedHTMLMessage
                  id="step.docUpload.rejectedSubtitle"
                  defaultMessage="We were unable to confirm your eligibility due to:"
                />
              </div>
              <ul className="sid-upload-rejection-list">
                {rejectionReasons.map((rejectionReason) => (
                  <li key={rejectionReason} className="sid-upload-rejection-list__li">
                    <FormattedHTMLMessage
                      id={`step.docUpload.rejectionReasons.${rejectionReason}`}
                      defaultMessage={`${rejectionReason}`}
                    />
                  </li>
                ))}
              </ul>
              <div className="sid-upload-rejection__next-step">
                <FormattedHTMLMessage
                  id="step.docUpload.rejectedNextStep"
                  defaultMessage="Please correct this and try again."
                />
              </div>
            </div>
          )}
        </div>
        {may24Variation === "doc-type-icons" && (
          <p>
            Documents must be dated within the current academic year OR no more than 90 days from
            today
          </p>
        )}
        {((assertionCheckListFlag && currentFileNames.length === 0) || !assertionCheckListFlag) && (
          <>
            <h2 className="sid-header__subtitle sid-l-space-btm-md sid-l-space-top-md">
              <FormattedHTMLMessage
                id={
                  useLegacyUploadMessages
                    ? "step.docUpload.uploadInstructions"
                    : "step.docUpload.documentsMustInclude"
                }
                defaultMessage="Your documents must include:"
              />
            </h2>
            <ul className="sid-doc-upload-requirements">
              {uploadInfo.map((i18nKey) => (
                <li key={i18nKey} className="sid-doc-upload-requirements__definition">
                  <FormattedHTMLMessage id={i18nKey} />
                </li>
              ))}
            </ul>

            {segment === SegmentEnum.AGE && country === GERMANY_COUNTRY_CODE && (
              <p className="sid-l-space-top-md">
                <FormattedHTMLMessage
                  id={"step.docUpload.uploadInfo.age.consentDE"}
                  defaultMessage="I consent to the processing and storage of my ID copy for age verification. I understand the image will automatically be saved as a copy, and that I am entitled to black out the serial number before uploading."
                />
              </p>
            )}
          </>
        )}
        {currentFileNames.length === 0 && uploadZone}
        {currentFileNames.length > 0 ? (
          <>
            <div className="sid-file-list sid-l-space-top-md">
              {currentFileNames.map((file, arrayIndex) => (
                <div className="sid-file-list__file" key={file.name}>
                  <div className="sid-file-list__thumbnail-wrapper">
                    {file.url ? (
                      <img
                        className="sid-file-list__thumbnail border-radius"
                        src={file.url}
                        alt="File thumbnail"
                      />
                    ) : (
                      <div className="sid-file-list__no-thumbnail">
                        <img
                          alt="File thumbnail"
                          src="https://assets-resources.sheerid.com/common/images/2018/icons/default-image.svg"
                        />
                      </div>
                    )}
                  </div>
                  <div className="sid-file-list__name-wrapper">
                    <div className="sid-file-list__name" title={file.name}>
                      {file.name}
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Remove file ${file.name}`}
                      className="sid-file-list__remove-btn"
                      onClick={() => removeFileAndUpdate(arrayIndex)}
                      onKeyPress={() => removeFileAndUpdate(arrayIndex)}
                    />
                  </div>
                </div>
              ))}

              {currentFileNames.length < MAX_DOC_UPLOAD_DOCS_ALLOWED &&
                may24Variation !== "doc-type-icons" && (
                  <>
                    {addingAnother ? (
                      <div className="sid-add-more-files">{uploadZone}</div>
                    ) : (
                      <div className="sid-file-list__file">
                        <div className="sid-file-list__thumbnail-wrapper thumbnail_wrapper_width">
                          {/* false positive, possibly because of the role=button */}
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label
                            className="sid-docdrop-wrap__dropzone thumbnail_wrapper"
                            htmlFor="sid-take-photo-input"
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                            role="button"
                            onKeyUp={(e) => handleAddFileOnKeyUp(e)}
                            tabIndex={0}
                          >
                            <PlusIcon className="sid-uploadzone__icon" width="16px" />
                            <p className="sid-docdrop-wrap__instructions thumbnail_wrapper-text">
                              <FormattedHTMLMessage
                                id="step.docUpload.addFile"
                                defaultMessage="Add documents"
                              />
                            </p>
                            <input
                              onChange={fileHandler}
                              type="file"
                              id="sid-take-photo-input"
                              hidden
                              accept="image/jpeg,image/png,application/pdf"
                              multiple
                              ref={(input: HTMLInputElement) => setRef("addFile", input)}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </>
                )}
            </div>

            <ConditionalErrorMsg
              fieldValidationErrors={fieldValidationErrors}
              errorId={errorId}
              erroredFileNames={viewModel.erroredFileNames}
            />

            {assertionCheckListFlag && currentFileNames.length >= 1 && (
              <RequirementsChecklist
                options={uploadInfo}
                selected={assertionCheckState}
                onChange={setAssertionCheckState}
                showError={showAssertionCheckErrors}
              />
            )}

            <div className="sid-submit sid-doc-upload-submit sid-l-space-top-md sid-submit-doc-upload_pb">
              <button
                id="sid-submit-doc-upload"
                onClick={submitForm}
                type="submit"
                className="sid-btn sid-btn--dark sid-doc-upload-submit-btn sid-submit__continue border-radius"
                aria-disabled={
                  currentFileNames.length <= 0 ||
                  (assertionCheckListFlag &&
                    !uploadInfo.every((req) => assertionCheckState.includes(req)))
                }
              >
                {may24Variation === "doc-type-icons" ? (
                  <FormattedHTMLMessage
                    id="_EXPERIMENTAL-VARIATION.step.docUpload.submitButtonLabel"
                    defaultMessage="Upload {count, plural,
                  one {document}
                  two {documents}
                  other {documents}
                }"
                    values={{ count: currentFileNames.length }}
                  />
                ) : (
                  <FormattedHTMLMessage
                    id="step.docUpload.submitButtonLabel"
                    defaultMessage="Submit"
                    values={{ count: currentFileNames.length }}
                  />
                )}
              </button>
            </div>
          </>
        ) : null}

        {may24Variation === "doc-type-icons" ? (
          <>
            {/* doc-type-icons is currently maxed out at 2 files. This needs to be sorted out per-segment */}
            {currentFileNames.length > 0 && currentFileNames.length < 2 && (
              <>
                <NamedDivider>Add another document</NamedDivider>
                {uploadZone}
              </>
            )}
            <h3>FAQ</h3>
            <ExpandingSection id="suggestedDocs" title="What information is needed?">
              <ul className="sid-doc-upload-requirements">
                {uploadInfo.map((i18nKey) => (
                  <li key={i18nKey} className="sid-doc-upload-requirements__definition">
                    <FormattedHTMLMessage id={i18nKey} />
                  </li>
                ))}
              </ul>
            </ExpandingSection>
          </>
        ) : (
          <>
            {flagDocConsiderations === "add-doc-considerations" &&
              isUSOnly &&
              (subSegment === "activeDuty" || subSegment === "reservist") && (
                <ExpandingSection
                  id="docConsiderations"
                  title={
                    <FormattedHTMLMessage
                      id="experiment.step.docUpload.documentConsiderations"
                      defaultMessage="Document considerations"
                    />
                  }
                  defaultState="open"
                >
                  <span className="sid-doc-upload-considerations">
                    <p>
                      <strong>
                        <FormattedHTMLMessage
                          id="step.docUpload.acceptableUploads.doNotUploadMilitaryIdTitle"
                          defaultMessage="Do not upload a photo of your military ID."
                        />
                      </strong>{" "}
                      <FormattedHTMLMessage
                        id="step.docUpload.acceptableUploads.doNotUploadMilitaryIdContent"
                        defaultMessage="In accordance with federal law, SheerID does not accept submissions of active duty/reservist military ID cards."
                      />
                    </p>
                  </span>
                </ExpandingSection>
              )}
            <ExpandingSection
              id="suggestedDocs"
              title={
                <FormattedHTMLMessage
                  id="step.docUpload.suggestedDocumentTypes"
                  defaultMessage="Suggested document types"
                />
              }
              defaultState={may24Variation === "checkbox-requirements" ? "closed" : "open"}
            >
              {useLegacyUploadMessages && (
                <FormattedHTMLMessage id="step.docUpload.acceptableUploadExamples" />
              )}
              <ul className="sid-acceptable-document-types">
                {(useLegacyUploadMessages ? getLegacyAcceptableUploads : getAcceptableUploads)(
                  segment,
                  subSegment,
                ).map((docType) => (
                  <li className="sid-acceptable-document-types__item" key={docType}>
                    <FormattedHTMLMessage id={docType} />
                  </li>
                ))}
                <li className="sid-acceptable-document-types__item">
                  <FormattedHTMLMessage id="step.docUpload.acceptedTypes" />
                </li>
              </ul>
              {/* Update consumer-facing docUpload language for Military - https://sheerid.atlassian.net/browse/HC-243
              only shown for US military programs and Active Duty and Reservist affiliations.
            */}
              {flagDocConsiderations !== "add-doc-considerations" &&
                isUSOnly &&
                (subSegment === "activeDuty" || subSegment === "reservist") && (
                  <div className="sid-acceptable-document-types">
                    <p>
                      <span className="display-block">
                        <strong>
                          <FormattedHTMLMessage
                            id="step.docUpload.acceptableUploads.doNotUploadMilitaryIdTitle"
                            defaultMessage="Do not upload a photo of your military ID."
                          />
                        </strong>
                      </span>
                      <FormattedHTMLMessage
                        id="step.docUpload.acceptableUploads.doNotUploadMilitaryIdContent"
                        defaultMessage="In accordance with federal law, SheerID does not accept submissions of active duty/reservist military ID cards."
                      />
                    </p>
                  </div>
                )}
            </ExpandingSection>
          </>
        )}

        <ExpandingSection
          id="whatHappensNext"
          title={
            <FormattedHTMLMessage
              id="step.docUpload.whatHappensNext"
              defaultMessage="What happens next?"
            />
          }
        >
          <p>
            <FormattedHTMLMessage
              id="step.docUpload.whoIsSheerId"
              defaultMessage="Document review is handled by SheerID, a trusted partner who specializes in verifying
            students."
            />
          </p>
          <p>
            <FormattedHTMLMessage
              id="step.docUpload.documentReviewProcess"
              defaultMessage="SheerID staff will review the documents for authenticity and compare the information in the documents against the information you provided."
            />
          </p>

          <p>
            <FormattedHTMLMessage
              id="step.docUpload.reviewTime"
              defaultMessage="Expected review time is {estimatedWaitTime}."
              values={{
                estimatedWaitTime: getEstAndMaxReviewTimes(verificationResponse, programTheme, intl)
                  .estReviewTime,
              }}
            />
          </p>

          <p>
            <FaqLinkComponent verificationService={verificationService} />
          </p>
        </ExpandingSection>

        <ExpandingSection
          id="canIFinishLater"
          title={
            <FormattedHTMLMessage
              id="step.docUpload.canIFinishLater"
              defaultMessage="Can I finish later?"
            />
          }
        >
          <p>
            <FormattedHTMLMessage
              id="step.docUpload.canIFinishLaterDetails"
              defaultMessage="Yes, If you don't finish now, we'll automatically send an email to the address you provided with a link back to this step."
            />
          </p>
        </ExpandingSection>

        {overrideEnabled && (
          <div className="sid-submit sid-override-submit sid-l-space-top-sm">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className="sid-link sid-override-link"
              onClick={() => cancelDocUpload()}
              role="button"
            >
              <FormattedHTMLMessage
                id="step.docUpload.cancelDocUploadLabel"
                defaultMessage="Click here if you are unable to verify with a document"
              />
            </a>
          </div>
        )}
      </div>
      <FormFooter verificationService={verificationService} showShield={false} />
    </div>
  );
};

export const StepDocUploadComponent = injectIntl(StepDocUploadRedesign);
